/**
 * A template: fill in the form, run, see the rows. Tabs on the right: Output (sample columns, then results),
 * About (what you get and the limits) and API (the same run from code).
 *
 *   /dashboard/templates/google-maps-leads
 *   /dashboard/templates/google-maps-leads?run=<runId>      opens a past run (and its inputs)
 *   /dashboard/templates/google-maps-leads?preset=<id>      fills the form from a saved preset
 */
import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import Head from "next/head";
import Link from "next/link";
import { useRouter } from "next/router";
import { Icon } from "@iconify/react";
import Layout from "@/layout/DashboardLayout";
import WorkspaceLayout from "@/components/layout/WorkspaceLayout";
import PageHeader from "@/components/layout/PageHeader";
import Button from "@/components/ui/Button";
import TabBar from "@/components/ui/TabBar";
import Dialog from "@/components/ui/Dialog";
import TextField from "@/components/ui/TextField";
import { ErrorState, Skeleton } from "@/components/ui/AsyncState";
import { RelativeTime, StatusText } from "@/components/ui/TableCells";
import SiteLogo from "@/components/templates/SiteLogo";
import TemplateForm, { FormValues, initialValues, templateFor, toApiInputs } from "@/components/templates/TemplateForm";
import RunResults, { ColumnsPreview } from "@/components/templates/RunResults";
import { useTemplateColumns } from "@/components/templates/useTemplateColumns";
import ApiSnippet from "@/components/templates/ApiSnippet";
import WatchSheet from "@/components/monitors/WatchSheet";
import { toast } from "@/context/ToastContext";
import { useUser } from "@/context/UserContext";
import { formatNumber } from "@/lib/format";
import { getScrapingPresetByUuid } from "@/services/scrapingPresets.service";
import {
  estimateTemplate,
  fetchTemplate,
  fetchTemplateRun,
  fetchTemplateRuns,
  runTemplate,
  saveTemplatePreset,
  updateTemplatePreset,
  TemplateColumn,
  TemplateDetail,
  TemplateInputError,
  TemplateRun,
  TemplateRunSummary,
} from "@/services/templates.service";

type Tab = "output" | "about" | "api";

function summarizeInputs(t: TemplateDetail, inputs: Record<string, unknown>): string {
  return t.inputs
    .filter(i => !i.advanced && inputs[i.key] !== undefined && inputs[i.key] !== "")
    .map(i => {
      const v = inputs[i.key];
      if (Array.isArray(v)) return v.length > 2 ? `${v.slice(0, 2).join(", ")} +${v.length - 2}` : v.join(", ");
      if (i.type === "select") return i.options?.find(o => o.value === v)?.label ?? String(v);
      return String(v);
    })
    .join(" · ");
}

const TemplatePage = () => {
  const router = useRouter();
  const slug = typeof router.query.slug === "string" ? router.query.slug : "";
  const { refetchUser } = useUser();
  const [template, setTemplate] = useState<TemplateDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [values, setValues] = useState<FormValues>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [estimate, setEstimate] = useState<{ credits: number | null; note: string | null } | null>(null);
  const [run, setRun] = useState<TemplateRun | null>(null);
  const [starting, setStarting] = useState(false);
  const [tab, setTab] = useState<Tab>("output");
  const [recent, setRecent] = useState<TemplateRunSummary[]>([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [presetName, setPresetName] = useState("");
  // Opened from a preset: its columns show from the start, and saving updates it (or saves a copy) instead of making a duplicate.
  const [preset, setPreset] = useState<{ id: string; columns: TemplateColumn[] | null } | null>(null);
  const presetId = preset?.id ?? null;
  const apiInputs = useMemo(() => (template ? toApiInputs(template.inputs, values) : {}), [template, values]);
  // Columns and sample for the chosen options (a Period, a tab, a switch): the run's own inputs once there is a run.
  const shown = useMemo(() => (template ? templateFor(template, run ? run.inputs : apiInputs) : null), [template, run, apiInputs]);
  const cols = useTemplateColumns(shown, preset);
  const [saving, setSaving] = useState(false);
  const [watchOpen, setWatchOpen] = useState(false);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  // Load the template, then fill the form from ?run, ?preset, or the template's examples.
  useEffect(() => {
    if (!router.isReady || !slug) return;
    let cancelled = false;
    setLoadError(null);
    (async () => {
      try {
        const t = await fetchTemplate(slug);
        if (cancelled) return;
        setTemplate(t);
        let saved: Record<string, unknown> | null = null;
        const runId = typeof router.query.run === "string" ? router.query.run : null;
        const presetId = typeof router.query.preset === "string" ? router.query.preset : null;
        if (runId) {
          try {
            const r = await fetchTemplateRun(runId);
            if (!cancelled && r.template === t.slug) {
              setRun(r);
              saved = r.inputs;
            }
          } catch {
            /* an old or foreign run id just opens the empty form */
          }
        }
        if (presetId) {
          try {
            const p = (await getScrapingPresetByUuid(presetId)) as unknown as {
              kind?: string;
              name?: string;
              template?: { slug?: string };
              inputs?: Record<string, unknown>;
              columns?: TemplateColumn[] | null;
            };
            if (!cancelled && p?.kind === "template" && p?.template?.slug === t.slug) {
              // A run made from the preset keeps that run's inputs in the form.
              if (!runId) saved = p.inputs ?? null;
              setPresetName(p.name ?? "");
              setPreset({ id: presetId, columns: p.columns ?? null });
            }
          } catch {
            /* ignore */
          }
        }
        if (!cancelled) setValues(initialValues(t, saved));
      } catch (err) {
        const res = (err as { response?: { status?: number; data?: { replacedBy?: string } } })?.response;
        // A template merged into another one opens that one (old links and bookmarks keep working).
        if (!cancelled && res?.status === 404 && res.data?.replacedBy) {
          router.replace({ pathname: router.pathname, query: { slug: res.data.replacedBy } });
          return;
        }
        const status = res?.status;
        if (!cancelled) setLoadError(status === 404 ? "There is no template here. It may have been renamed or retired." : "This template couldn't be loaded.");
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, slug]);

  const loadRecent = useCallback(() => {
    if (!slug) return;
    fetchTemplateRuns({ template: slug, limit: 5 })
      .then(r => setRecent(r.runs))
      .catch(() => setRecent([]));
  }, [slug]);
  useEffect(loadRecent, [loadRecent]);

  // Credit estimate, a moment after the form stops changing.
  useEffect(() => {
    if (!template) return;
    const id = setTimeout(() => {
      estimateTemplate(template.slug, apiInputs)
        .then(e => setEstimate({ credits: e.credits, note: e.note }))
        .catch(() => setEstimate(null));
    }, 400);
    return () => clearTimeout(id);
  }, [template, apiInputs]);

  // Poll a running run.
  useEffect(() => {
    if (!run || run.status !== "running") return;
    pollRef.current = setTimeout(async () => {
      try {
        const next = await fetchTemplateRun(run.runId);
        setRun(next);
        if (next.status !== "running") {
          loadRecent();
          void refetchUser(); // the sidebar's credit meter
        }
      } catch {
        setRun({ ...run }); // try again on the next tick
      }
    }, 2500);
    return () => {
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, [run, loadRecent, refetchUser]);

  const start = async () => {
    if (!template) return;
    setErrors({});
    setFormError(null);
    setStarting(true);
    setTab("output");
    try {
      const r = await runTemplate(template.slug, apiInputs);
      setRun(r);
      router.replace({ pathname: router.pathname, query: { slug, run: r.runId, ...(presetId ? { preset: presetId } : {}) } }, undefined, { shallow: true, scroll: false });
      if (window.matchMedia("(max-width: 1023px)").matches) setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
      loadRecent();
    } catch (err) {
      if (err instanceof TemplateInputError) {
        setErrors(err.fields);
        setFormError(err.message);
      } else setFormError(err instanceof Error ? err.message : "The run couldn't be started.");
    } finally {
      setStarting(false);
    }
  };

  const savePreset = async (asNew = false) => {
    if (!template || !presetName.trim()) return;
    setSaving(true);
    // The template's own columns are the default, so only a changed choice is stored.
    const columns = cols.isCustom ? cols.columns : undefined;
    try {
      if (presetId && !asNew) {
        await updateTemplatePreset(presetId, { name: presetName.trim(), inputs: apiInputs, columns: columns ?? null });
        toast.success("Preset updated.");
      } else {
        const id = await saveTemplatePreset({ name: presetName.trim(), templateSlug: template.slug, inputs: apiInputs, columns });
        // The page now edits the new preset; its columns are the ones on screen.
        if (id) {
          setPreset({ id, columns: columns ?? null });
          router.replace({ pathname: router.pathname, query: { slug, preset: id } }, undefined, { shallow: true });
        }
        toast.success("Preset saved. Find it under Presets.");
      }
      setSaveOpen(false);
    } catch (err) {
      if (err instanceof TemplateInputError) {
        setErrors(err.fields);
        setSaveOpen(false);
        setFormError("Fix the highlighted fields, then save again.");
      } else toast.error(err instanceof Error ? err.message : "The preset couldn't be saved.");
    } finally {
      setSaving(false);
    }
  };

  if (loadError) {
    return (
      <>
        <Head>
          <title>Template | Spidra</title>
        </Head>
        <PageHeader title="Template" showBack onBack={() => router.push("/dashboard/templates")} backLabel="Templates" />
        <ErrorState title="Couldn't open this template" error={loadError} onRetry={() => router.reload()} />
      </>
    );
  }

  if (!template) {
    return (
      <div className="space-y-5" role="status" aria-label="Loading template">
        <Skeleton className="h-12 w-80" />
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-5">
          <Skeleton className="h-96 lg:col-span-2" />
          <Skeleton className="h-96 lg:col-span-3" />
        </div>
      </div>
    );
  }

  const running = run?.status === "running";
  // The estimate's note says what can add to it (a second step, AI extraction), so it is shown in full.
  const costLine =
    estimate?.credits != null
      ? `Costs about ${formatNumber(estimate.credits)} ${estimate.credits === 1 ? "credit" : "credits"}${estimate.note ? `, ${estimate.note}` : ""}.${/fail/i.test(estimate.note ?? "") ? "" : " Failed pages are free."}`
      : "Failed pages are free.";

  // Logo, site and title share one row with the actions, centred on it; the summary sits under the title's text.
  // On a phone the actions move below the summary.
  const header = (
    <div className="grid shrink-0 gap-x-4 gap-y-2 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
      <div className="flex min-w-0 items-center gap-3.5">
        <SiteLogo site={template.site} tool={template.tool} size="lg" />
        <div className="min-w-0">
          <p className="flex flex-wrap items-center gap-x-2 text-13 text-fg-muted">
            {template.site?.name ?? "Any website"}
            <span aria-hidden="true">·</span>
            <span className="inline-flex items-center gap-1 text-success-text">
              <Icon icon="lucide:badge-check" className="text-sm" aria-hidden="true" />
              Maintained by Spidra
            </span>
          </p>
          <h1 className="mt-0.5 text-2xl font-semibold leading-tight tracking-tight text-fg">{template.title}</h1>
        </div>
      </div>
      <div className="order-3 mt-1 flex shrink-0 items-center gap-2 sm:order-none sm:mt-0">
        <Button variant="secondary" size="sm" leftIcon="lucide:bookmark-plus" onClick={() => setSaveOpen(true)}>
          {presetId ? "Update preset" : "Save as preset"}
        </Button>
        {template.hasRows && (
          <Button variant="secondary" size="sm" leftIcon="lucide:radar" onClick={() => setWatchOpen(true)}>
            Watch for changes
          </Button>
        )}
      </div>
      <p className="order-2 max-w-2xl text-sm text-fg-secondary sm:order-none sm:pl-[3.875rem]">{template.summary}</p>
    </div>
  );

  // An admin's notice or pause on this template, shown above the form.
  const availability = template.availability ?? null;
  const pausedNow = availability?.state === "off";

  const form = (
    <div className="space-y-5">
      {availability && (
        <p
          role="status"
          className={`flex items-start gap-2 rounded-control px-3 py-2.5 text-13 ${pausedNow ? "bg-danger-subtle text-danger-text" : "bg-warning-subtle text-warning-text"}`}
        >
          <Icon icon={pausedNow ? "lucide:circle-pause" : "lucide:info"} className="mt-0.5 shrink-0" aria-hidden="true" />
          <span>{availability.message || (pausedNow ? "This template is paused for maintenance. Please try again later." : "This template may be slower than usual right now.")}</span>
        </p>
      )}
      <TemplateForm inputs={template.inputs} values={values} onChange={v => setValues(v)} errors={errors} />
      {formError && (
        <p className="flex items-start gap-1.5 rounded-control bg-danger-subtle px-3 py-2 text-13 text-danger-text" role="alert">
          <Icon icon="lucide:circle-alert" className="mt-0.5 shrink-0" aria-hidden="true" />
          {formError}
        </p>
      )}
    </div>
  );

  const formFooter = (
    <div className="space-y-2">
      <Button type="submit" form="template-form" fullWidth loading={starting} disabled={running || pausedNow} leftIcon={running ? undefined : pausedNow ? "lucide:circle-pause" : "lucide:play"}>
        {running ? "Running…" : pausedNow ? "Paused" : "Run"}
      </Button>
      <p className="text-center text-xs text-fg-muted" aria-live="polite">
        {costLine}
      </p>
    </div>
  );

  const outputTab = run ? (
    <RunResults template={shown ?? template} run={run} columns={cols.columns} onColumnsChange={cols.setColumns} />
  ) : (
    <div className="space-y-6">
      {template.hasRows ? (
        <ColumnsPreview template={shown ?? template} columns={cols.columns} onColumnsChange={cols.setColumns} />
      ) : (
        <p className="text-13 text-fg-secondary">
          This template runs in the background. When it finishes, you can open the full results on the {template.tool === "crawl" ? "Crawl" : "Parse"} page.
        </p>
      )}
      {recent.length > 0 && (
        <section aria-labelledby="recent-runs">
          <h2 id="recent-runs" className="mb-2 text-sm font-semibold text-fg">
            Your recent runs
          </h2>
          <ul className="divide-y divide-line overflow-hidden rounded-control border border-line">
            {recent.map(r => (
              <li key={r.runId}>
                <Link
                  href={{ pathname: router.pathname, query: { slug, run: r.runId, ...(presetId ? { preset: presetId } : {}) } }}
                  onClick={() => fetchTemplateRun(r.runId).then(full => { setRun(full); setValues(initialValues(template, full.inputs)); })}
                  className="flex items-center justify-between gap-3 px-3 py-2.5 text-13 hover:bg-surface-muted"
                >
                  <span className="min-w-0 truncate text-fg">{summarizeInputs(template, r.inputs) || "Run"}</span>
                  <span className="flex shrink-0 items-center gap-3">
                    <StatusText status={r.status} className="text-13" />
                    <RelativeTime value={r.createdAt} className="text-13" />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );

  const aboutTab = (
    <div className="max-w-2xl space-y-5 text-sm leading-6 text-fg-secondary">
      <p>{template.description}</p>
      {template.hasRows && (
        <div>
          <h3 className="mb-2 font-semibold text-fg">What you get</h3>
          <ul className="flex flex-wrap gap-1.5">
            {(shown ?? template).columns
              .filter(c => !c.key.startsWith("_"))
              .map(c => (
                <li key={c.key} className="rounded-md bg-surface-muted px-2 py-0.5 text-xs text-fg-secondary ring-1 ring-inset ring-line">
                  {c.label}
                </li>
              ))}
          </ul>
          <p className="mt-2 text-13 text-fg-muted">The JSON download includes every field, not only the columns shown.</p>
        </div>
      )}
      {template.limits.length > 0 && (
        <div>
          <h3 className="mb-2 font-semibold text-fg">Good to know</h3>
          <ul className="list-disc space-y-1 pl-5">
            {template.limits.map(l => (
              <li key={l}>{l}</li>
            ))}
          </ul>
        </div>
      )}
      <div>
        <h3 className="mb-2 font-semibold text-fg">Cost</h3>
        <p>
          {template.tool === "batch"
            ? "1 credit per page fetched" + (template.usesAi ? ", plus a few credits per page for AI extraction" : "") + ". Pages that fail are not charged."
            : template.tool === "search"
              ? "1 credit per 10 results, plus 1 credit per page read. Searches with no results are free."
              : template.tool === "map"
                ? "1 credit per run, nothing if no URLs are found."
                : template.tool === "crawl"
                  ? "1 credit per page returned. Pages the crawler only passes through are free."
                  : "1 credit per page of the document."}
        </p>
      </div>
    </div>
  );

  const results = (
    <div ref={resultsRef} className="flex min-h-0 flex-1 flex-col">
      <TabBar
        label="Template views"
        idPrefix="template"
        value={tab}
        onChange={setTab}
        tabs={[
          { value: "output", label: run ? "Results" : "Output" },
          { value: "about", label: "About" },
          { value: "api", label: "API" },
        ]}
        className="shrink-0 px-5 sm:px-6"
      />
      <div id="template-panel" role="tabpanel" aria-labelledby={`template-tab-${tab}`} className="flex min-h-0 flex-1 flex-col overflow-auto p-5 sm:p-6">
        {tab === "output" ? outputTab : tab === "about" ? aboutTab : <ApiSnippet slug={template.slug} inputs={apiInputs} />}
      </div>
    </div>
  );

  return (
    <>
      <Head>
        <title>{`${template.title} | Spidra`}</title>
      </Head>
      <WorkspaceLayout
        header={header}
        form={form}
        formFooter={formFooter}
        formProps={{
          id: "template-form",
          onSubmit: e => {
            e.preventDefault();
            if (!starting && !running) void start();
          },
        }}
        results={results}
        resultsScroll={false}
        resultsClassName="!p-0"
        split="form-narrow"
        formLabel="Inputs"
      />

      <WatchSheet
        open={watchOpen}
        onClose={() => setWatchOpen(false)}
        initialSources={[{ kind: "template", template: template.slug, inputs: apiInputs, label: template.title, site: template.site }]}
      />
      <Dialog
        isOpen={saveOpen}
        onClose={() => setSaveOpen(false)}
        title={presetId ? "Update preset" : "Save as preset"}
        description={
          presetId
            ? "Save these inputs and columns to this preset, or keep it as it is and save a new one."
            : "Keep these inputs and columns so you can run them again in one click, from the dashboard or the API."
        }
        width="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setSaveOpen(false)}>
              Cancel
            </Button>
            {presetId && (
              <Button variant="secondary" loading={saving} disabled={!presetName.trim()} onClick={() => savePreset(true)}>
                Save as new
              </Button>
            )}
            <Button loading={saving} disabled={!presetName.trim()} onClick={() => savePreset()}>
              {presetId ? "Update preset" : "Save preset"}
            </Button>
          </>
        }
      >
        <TextField
          label="Name"
          value={presetName}
          placeholder={`${template.title}: ${summarizeInputs(template, apiInputs)}`.slice(0, 80)}
          onChange={e => setPresetName(e.target.value)}
          data-autofocus
          onKeyDown={e => {
            if (e.key === "Enter") void savePreset();
          }}
        />
      </Dialog>
    </>
  );
};

export default TemplatePage;

TemplatePage.getLayout = function getLayout(page: ReactElement) {
  return <Layout>{page}</Layout>;
};
